// frontend/src/pages/RegulatoryLibrary.jsx
// Week 6: Regulatory Reference Management Module
// Week 7 patch: Updated badge, notification link
// Week 8 patch: Org notes — inline display, Add/Edit/Delete, 500 char counter, stale flag
// commit: feat(w8): add org notes to regulatory library with stale version detection

import React, { useState, useEffect, useCallback } from 'react';
import {
  getRegulatoryRefs,
  updateRegulatoryRef,
  getRegulatoryRefHistory,
  getNotifications,
  getOrgNotes,
  upsertOrgNote,
  deleteOrgNote,
} from '../api';

// ─── Palette ──────────────────────────────────────────────────────────────────
const NAVY   = '#132e50';
const GOLD   = '#b8860b';
const CREAM  = '#f5f2ed';
const BORDER = '#e8e4de';
const MAX_NOTE = 500;

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtDatetime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function extractUpdatedCodes(notifications) {
  const codes = new Set();
  for (const n of notifications) {
    if (n.type === 'clause_updated' && !n.dismissed_at && !n.is_read) {
      const match = n.title.match(/Regulatory clause updated\s*[—–-]\s*(.+)$/i);
      if (match) codes.add(match[1].trim());
    }
  }
  return codes;
}

/**
 * Build a map of regulatory_ref_id → note object from the notes array.
 */
function buildNotesMap(notes) {
  return (notes || []).reduce((acc, n) => {
    acc[n.regulatory_ref_id] = n;
    return acc;
  }, {});
}

/**
 * Returns true if the note was saved against an older clause version.
 */
function isNoteStale(note, clause) {
  if (!note) return false;
  return note.clause_version_at_save < clause.version;
}

// ─── Components ───────────────────────────────────────────────────────────────

function PageHeader({ title, subtitle }) {
  return (
    <div style={{ padding: '32px 40px 24px', borderBottom: `1px solid ${BORDER}`, background: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <div style={{ width: 4, height: 28, background: GOLD, borderRadius: 1 }} />
        <h1 style={{ fontSize: 22, fontWeight: 700, color: NAVY, margin: 0 }}>{title}</h1>
      </div>
      {subtitle && <p style={{ fontSize: 13, color: '#5a5a5a', margin: '0 0 0 16px', lineHeight: 1.5 }}>{subtitle}</p>}
    </div>
  );
}

function Badge({ label, bg, color, border }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 3,
      fontSize: 10, fontWeight: 700, letterSpacing: 0.3,
      background: bg, color: color,
      border: border ? `1px solid ${border}` : 'none',
    }}>
      {label}
    </span>
  );
}

// ─── Note form — inline textarea with char counter ────────────────────────────
function NoteForm({ initialValue, onSave, onCancel, saving }) {
  const [text, setText] = useState(initialValue || '');
  const remaining = MAX_NOTE - text.length;
  const counterColor = remaining <= 20 ? '#e53e3e' : remaining <= 100 ? '#d97706' : '#9ca3af';

  function handleSubmit(e) {
    e.preventDefault();
    if (!text.trim()) return;
    onSave(text.trim());
  }

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: 8 }}>
      <textarea
        value={text}
        onChange={e => setText(e.target.value.slice(0, MAX_NOTE))}
        placeholder="Add your organisation's context, exemptions, or internal reference for this clause…"
        rows={3}
        style={{
          width: '100%',
          padding: '8px 10px',
          fontSize: 12,
          color: '#1a1a1a',
          border: `1px solid ${BORDER}`,
          borderRadius: 4,
          background: '#fff',
          fontFamily: 'inherit',
          resize: 'vertical',
          lineHeight: 1.6,
          boxSizing: 'border-box',
          outline: 'none',
        }}
      />
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: 6,
      }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="submit"
            disabled={saving || !text.trim()}
            style={{
              padding: '5px 14px', borderRadius: 4, fontSize: 11, fontWeight: 700,
              cursor: saving || !text.trim() ? 'not-allowed' : 'pointer',
              background: saving || !text.trim() ? '#8a7a40' : GOLD,
              color: '#fff', border: 'none',
            }}
          >
            {saving ? 'Saving…' : 'Save note'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            style={{
              padding: '5px 14px', borderRadius: 4, fontSize: 11, fontWeight: 600,
              cursor: 'pointer', background: 'transparent',
              color: '#5a5a5a', border: `1px solid ${BORDER}`,
            }}
          >
            Cancel
          </button>
        </div>
        <span style={{ fontSize: 11, color: counterColor, fontWeight: remaining <= 100 ? 600 : 400 }}>
          {remaining}/{MAX_NOTE}
        </span>
      </div>
    </form>
  );
}

// ─── Inline note display ──────────────────────────────────────────────────────
function NoteDisplay({ note, clause, isAdmin, isPlatformOwner, onEdit, onDelete }) {
  const stale = isNoteStale(note, clause);

  return (
    <div style={{
      marginTop: 10,
      borderLeft: `3px solid ${stale ? '#d97706' : GOLD}`,
      background: stale ? '#fffbeb' : '#fffdf9',
      borderRadius: '0 4px 4px 0',
      padding: '10px 12px',
    }}>
      {stale && (
        <div style={{
          fontSize: 11, fontWeight: 600, color: '#92400e',
          marginBottom: 6,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <span>⚠</span>
          <span>
            Note written against v{note.clause_version_at_save} — clause updated to v{clause.version} on {fmtDate(clause.updated_at)}. Consider reviewing.
          </span>
        </div>
      )}
      <div style={{ fontSize: 11, color: '#3a3a3a', lineHeight: 1.6, marginBottom: 6 }}>
        {note.note}
      </div>
      <div style={{
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <span style={{ fontSize: 10, color: '#9ca3af' }}>
          {note.updated_by_name || 'Unknown'} · {fmtDate(note.updated_at)}
        </span>
        {isAdmin && !isPlatformOwner && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={onEdit}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 11, color: GOLD, fontWeight: 600, padding: 0,
              }}
            >
              Edit
            </button>
            <button
              onClick={onDelete}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 11, color: '#e53e3e', fontWeight: 600, padding: 0,
              }}
            >
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Edit form — clause edit (platform owner only) ────────────────────────────
function EditForm({ clause, onSave, onCancel, saving }) {
  const [form, setForm] = useState({
    title:         clause.title,
    standard:      clause.standard,
    paragraphs:    clause.paragraphs,
    obligation:    clause.obligation,
    display_order: clause.display_order,
    change_note:   '',
  });
  const [error, setError] = useState('');

  function set(field, value) { setForm(f => ({ ...f, [field]: value })); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim() || !form.standard.trim() || !form.paragraphs.trim() || !form.obligation.trim()) {
      setError('Title, standard, paragraphs, and obligation are all required.');
      return;
    }
    setError('');
    await onSave(form);
  }

  const labelStyle = {
    display: 'block', fontSize: 11, fontWeight: 700,
    color: NAVY, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.3,
  };
  const inputStyle = {
    width: '100%', padding: '8px 10px', fontSize: 12, color: '#1a1a1a',
    border: `1px solid ${BORDER}`, borderRadius: 4, background: '#fff',
    fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
  };

  return (
    <form onSubmit={handleSubmit} style={{
      background: '#fffdf9', border: `1px solid ${GOLD}33`,
      borderRadius: 6, padding: '20px 24px', marginTop: 8,
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: GOLD, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Editing clause — version {clause.version} → {clause.version + 1}
      </div>
      {error && (
        <div style={{
          background: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: 4,
          padding: '8px 12px', fontSize: 12, color: '#721c24', marginBottom: 14,
        }}>
          {error}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={labelStyle}>Title</label>
          <input style={inputStyle} value={form.title} onChange={e => set('title', e.target.value)} />
        </div>
        <div>
          <label style={labelStyle}>Standard</label>
          <input style={inputStyle} value={form.standard} onChange={e => set('standard', e.target.value)} />
        </div>
        <div>
          <label style={labelStyle}>Display order</label>
          <input style={inputStyle} type="number" value={form.display_order} onChange={e => set('display_order', e.target.value)} />
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Paragraph references</label>
        <input style={inputStyle} value={form.paragraphs} onChange={e => set('paragraphs', e.target.value)} />
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Board obligation text</label>
        <textarea style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6, minHeight: 100 }} value={form.obligation} onChange={e => set('obligation', e.target.value)} />
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Change note (optional)</label>
        <input style={inputStyle} value={form.change_note} onChange={e => set('change_note', e.target.value)} placeholder="e.g. Updated to reflect APRA CPS 230 April 2026 amendment" />
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button type="submit" disabled={saving} style={{
          padding: '8px 20px', borderRadius: 4, fontSize: 12, fontWeight: 700,
          cursor: saving ? 'not-allowed' : 'pointer',
          background: saving ? '#8a7a40' : GOLD, color: '#fff', border: 'none',
        }}>
          {saving ? 'Saving…' : 'Save clause'}
        </button>
        <button type="button" onClick={onCancel} disabled={saving} style={{
          padding: '8px 20px', borderRadius: 4, fontSize: 12, fontWeight: 600,
          cursor: 'pointer', background: 'transparent', color: '#5a5a5a', border: `1px solid ${BORDER}`,
        }}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ─── Version history drawer ───────────────────────────────────────────────────
function VersionHistory({ history, loading, onClose }) {
  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, bottom: 0, width: 520,
      background: '#fff', borderLeft: `1px solid ${BORDER}`,
      boxShadow: '-4px 0 24px rgba(0,0,0,0.10)', zIndex: 100,
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{
        padding: '20px 24px', borderBottom: `1px solid ${BORDER}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: NAVY }}>Version History</div>
          <div style={{ fontSize: 11, color: '#6b6b6b', marginTop: 2 }}>All prior versions — newest first</div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b6b6b', padding: '0 4px' }}>×</button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
        {loading ? (
          <div style={{ color: '#8a8a8a', fontSize: 12, textAlign: 'center', paddingTop: 40 }}>Loading history…</div>
        ) : history.length === 0 ? (
          <div style={{ background: CREAM, borderRadius: 6, padding: 20, fontSize: 12, color: '#8a8a8a', textAlign: 'center' }}>
            No prior versions. This clause has not been edited since it was seeded.
          </div>
        ) : (
          history.map((v, idx) => (
            <div key={v.id} style={{
              border: `1px solid ${BORDER}`, borderRadius: 6, padding: '14px 16px',
              marginBottom: 12, background: idx === 0 ? '#fffdf9' : '#fff',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <Badge label={`v${v.version}`} bg={NAVY} color="#b8860b" />
                <span style={{ fontSize: 11, color: '#8a8a8a' }}>{fmtDatetime(v.changed_at)}</span>
              </div>
              <div style={{ fontSize: 11, color: '#5a5a5a', marginBottom: 6 }}>
                Changed by: <strong style={{ color: NAVY }}>{v.changed_by_name || 'Unknown'}</strong>
              </div>
              {v.change_note && (
                <div style={{
                  background: '#f8f6f2', borderLeft: `3px solid ${GOLD}`,
                  padding: '6px 10px', fontSize: 11, color: '#3a3a3a',
                  borderRadius: '0 4px 4px 0', marginBottom: 8,
                }}>
                  {v.change_note}
                </div>
              )}
              <div style={{ fontSize: 11, fontWeight: 700, color: '#5a5a5a', marginBottom: 2 }}>Standard: <span style={{ fontWeight: 400 }}>{v.standard}</span></div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#5a5a5a', marginBottom: 6 }}>Paragraphs: <span style={{ fontWeight: 400 }}>{v.paragraphs}</span></div>
              <div style={{ fontSize: 11, color: '#3a3a3a', lineHeight: 1.6, background: CREAM, borderRadius: 4, padding: '8px 10px' }}>
                {v.obligation}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ─── Clause card ──────────────────────────────────────────────────────────────
function ClauseCard({
  clause, isAdmin, isPlatformOwner, isEditing, isSaving,
  onEdit, onSave, onCancelEdit, onViewHistory,
  isPlatformDefault, hasUpdate,
  orgNote, onSaveNote, onDeleteNote, savingNote,
}) {
  const [editingNote, setEditingNote] = useState(false);
  const stale = isNoteStale(orgNote, clause);

  function handleSaveNote(text) {
    onSaveNote(clause.id, text).then(() => setEditingNote(false));
  }

  function handleDeleteNote() {
    if (window.confirm('Delete this note? This cannot be undone.')) {
      onDeleteNote(clause.id);
    }
  }

  return (
    <div style={{
      border: hasUpdate ? `1px solid ${GOLD}88` : `1px solid ${BORDER}`,
      borderRadius: 6, marginBottom: 10, overflow: 'hidden',
      boxShadow: isEditing ? `0 0 0 2px ${GOLD}44` : hasUpdate ? `0 0 0 1px ${GOLD}33` : 'none',
    }}>
      {/* Header bar */}
      <div style={{
        background: NAVY, display: 'grid',
        gridTemplateColumns: '160px 140px 160px 1fr auto',
        alignItems: 'center',
      }}>
        <div style={{ padding: '8px 12px', fontSize: 10, fontWeight: 700, color: GOLD, letterSpacing: 0.3, textTransform: 'uppercase' }}>Condition</div>
        <div style={{ padding: '8px 12px', fontSize: 10, fontWeight: 700, color: '#c8d6e8', letterSpacing: 0.3, textTransform: 'uppercase' }}>Standard</div>
        <div style={{ padding: '8px 12px', fontSize: 10, fontWeight: 700, color: '#c8d6e8', letterSpacing: 0.3, textTransform: 'uppercase' }}>Paragraphs</div>
        <div style={{ padding: '8px 12px', fontSize: 10, fontWeight: 700, color: '#c8d6e8', letterSpacing: 0.3, textTransform: 'uppercase' }}>Board Obligation</div>
        <div style={{ padding: '8px 12px' }} />
      </div>

      {/* Data row */}
      <div style={{
        background: hasUpdate ? '#fffdf4' : '#fff',
        display: 'grid',
        gridTemplateColumns: '160px 140px 160px 1fr auto',
        borderTop: `1px solid ${BORDER}`,
        alignItems: 'start',
      }}>
        <div style={{ padding: '12px 12px' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: GOLD }}>{clause.condition_code}</div>
          <div style={{ fontSize: 9, color: '#8a8a8a', marginTop: 2 }}>{clause.title}</div>
          <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Badge label={`v${clause.version}`} bg="#e8f0ff" color="#1a3a8a" />
            {isPlatformDefault && <Badge label="Platform default" bg="#e2e3e5" color="#383d41" />}
            {hasUpdate && <Badge label="Updated" bg={GOLD} color="#fff" />}
            {orgNote && <Badge label={stale ? "Note — review" : "Note"} bg={stale ? '#fef3c7' : '#f0fdf4'} color={stale ? '#92400e' : '#166534'} />}
          </div>
        </div>
        <div style={{ padding: '12px 12px', fontSize: 10, fontWeight: 700, color: NAVY }}>{clause.standard}</div>
        <div style={{ padding: '12px 12px', fontSize: 10, color: '#5a5a5a' }}>{clause.paragraphs}</div>

        {/* Obligation + inline note */}
        <div style={{ padding: '12px 12px' }}>
          <div style={{ fontSize: 11, color: '#3a3a3a', lineHeight: 1.6 }}>{clause.obligation}</div>

          {/* Existing note display */}
          {orgNote && !editingNote && (
            <NoteDisplay
              note={orgNote}
              clause={clause}
              isAdmin={isAdmin}
              isPlatformOwner={isPlatformOwner}
              onEdit={() => setEditingNote(true)}
              onDelete={handleDeleteNote}
            />
          )}

          {/* Note edit/add form */}
          {isAdmin && !isPlatformOwner && editingNote && (
            <NoteForm
              initialValue={orgNote?.note || ''}
              onSave={handleSaveNote}
              onCancel={() => setEditingNote(false)}
              saving={savingNote}
            />
          )}
        </div>

        {/* Action buttons */}
        <div style={{ padding: '12px 12px', display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end', minWidth: 120 }}>
          <button
            onClick={() => onViewHistory(clause.id)}
            style={{
              padding: '5px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
              background: 'transparent', color: '#5a5a5a', border: `1px solid ${BORDER}`,
              borderRadius: 4, whiteSpace: 'nowrap',
            }}
          >
            History
          </button>

          {/* Edit clause — platform owner only */}
          {isAdmin && isPlatformOwner && !isEditing && (
            <button
              onClick={() => onEdit(clause)}
              style={{
                padding: '5px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                background: GOLD, color: '#fff', border: 'none',
                borderRadius: 4, whiteSpace: 'nowrap',
              }}
            >
              Edit
            </button>
          )}

          {/* Add / Edit note — client admin only */}
          {isAdmin && !isPlatformOwner && !editingNote && (
            <button
              onClick={() => setEditingNote(true)}
              style={{
                padding: '5px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                background: orgNote ? 'transparent' : NAVY,
                color: orgNote ? GOLD : '#fff',
                border: orgNote ? `1px solid ${GOLD}` : 'none',
                borderRadius: 4, whiteSpace: 'nowrap',
              }}
            >
              {orgNote ? 'Edit note' : 'Add note'}
            </button>
          )}
        </div>
      </div>

      {/* Clause edit form — platform owner only */}
      {isEditing && (
        <div style={{ padding: '0 12px 12px', background: '#fff' }}>
          <EditForm
            clause={clause}
            onSave={onSave}
            onCancel={onCancelEdit}
            saving={isSaving}
          />
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function RegulatoryLibrary({ user }) {
  const isAdmin         = user?.role === 'admin';
  const isPlatformOwner = user?.orgId === process.env.REACT_APP_PLATFORM_OWNER_ORG_ID;

  const [refs,             setRefs]             = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [error,            setError]            = useState('');
  const [editingId,        setEditingId]        = useState(null);
  const [savingId,         setSavingId]         = useState(null);
  const [saveError,        setSaveError]        = useState('');
  const [historyRefId,     setHistoryRefId]     = useState(null);
  const [history,          setHistory]          = useState([]);
  const [historyLoading,   setHistoryLoading]   = useState(false);
  const [successMsg,       setSuccessMsg]       = useState('');
  const [updatedCodes,     setUpdatedCodes]     = useState(new Set());
  const [notesMap,         setNotesMap]         = useState({});
  const [savingNote,       setSavingNote]       = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [refsData, notifData, notesData] = await Promise.all([
        getRegulatoryRefs(),
        getNotifications().catch(() => ({ notifications: [] })),
        getOrgNotes().catch(() => ({ notes: [] })),
      ]);
      setRefs(refsData.refs || []);
      setUpdatedCodes(extractUpdatedCodes(notifData.notifications || []));
      setNotesMap(buildNotesMap(notesData.notes || []));
    } catch (err) {
      setError(err.message || 'Failed to load regulatory references.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const grouped = refs.reduce((acc, r) => {
    if (!acc[r.condition_code]) acc[r.condition_code] = [];
    acc[r.condition_code].push(r);
    return acc;
  }, {});

  async function handleSave(clauseId, form) {
    setSavingId(clauseId);
    setSaveError('');
    try {
      await updateRegulatoryRef(clauseId, form);
      setEditingId(null);
      setSuccessMsg('Clause updated. A new version has been recorded.');
      setTimeout(() => setSuccessMsg(''), 4000);
      const clause = refs.find(r => r.id === clauseId);
      if (clause) {
        setUpdatedCodes(prev => {
          const next = new Set(prev);
          next.delete(clause.condition_code);
          return next;
        });
      }
      await loadAll();
    } catch (err) {
      setSaveError(err.message || 'Failed to save clause.');
    } finally {
      setSavingId(null);
    }
  }

  async function handleSaveNote(refId, text) {
    setSavingNote(true);
    try {
      const data = await upsertOrgNote(refId, { note: text });
      setNotesMap(prev => ({ ...prev, [refId]: data.note }));
    } catch (err) {
      console.error('Note save failed:', err.message);
    } finally {
      setSavingNote(false);
    }
  }

  async function handleDeleteNote(refId) {
    try {
      await deleteOrgNote(refId);
      setNotesMap(prev => {
        const next = { ...prev };
        delete next[refId];
        return next;
      });
    } catch (err) {
      console.error('Note delete failed:', err.message);
    }
  }

  async function handleViewHistory(refId) {
    setHistoryRefId(refId);
    setHistory([]);
    setHistoryLoading(true);
    try {
      const data = await getRegulatoryRefHistory(refId);
      setHistory(data.history || []);
    } catch (_) {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: CREAM }}>
      <PageHeader
        title="Regulatory Library"
        subtitle="APRA prudential standard obligations mapped to each governance condition code. Admin users can edit clause text; every change creates a versioned audit record."
      />

      <div style={{ padding: '28px 40px' }}>

        {/* Updated clauses notice */}
        {updatedCodes.size > 0 && (
          <div style={{
            background: '#fffbeb', border: `1px solid ${GOLD}66`,
            borderLeft: `4px solid ${GOLD}`, borderRadius: '0 6px 6px 0',
            padding: '12px 16px', fontSize: 12, color: '#6b4c00',
            marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <span style={{ fontSize: 16 }}>📋</span>
            <span>
              {updatedCodes.size === 1
                ? `1 clause has been updated and requires your review.`
                : `${updatedCodes.size} clauses have been updated and require your review.`}
              {' '}Affected clauses are highlighted below with an <strong>Updated</strong> badge.
            </span>
          </div>
        )}

        {/* Role notice for non-admins */}
        {!isAdmin && updatedCodes.size === 0 && (
          <div style={{
            background: '#e8f0ff', border: '1px solid #c8d8f8', borderRadius: 6,
            padding: '10px 16px', fontSize: 12, color: '#1a3a8a',
            marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span>§</span>
            <span>You are viewing the current regulatory reference library. Only admin users can edit clauses.</span>
          </div>
        )}

        {/* Admin notice — platform owner only */}
        {isAdmin && isPlatformOwner && (
          <div style={{
            background: '#fffdf9', border: `1px solid ${GOLD}44`, borderRadius: 6,
            padding: '10px 16px', fontSize: 12, color: '#6b4c00',
            marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span>§</span>
            <span>
              Editing a platform default clause creates an org-specific override for your organisation.
              The platform default is preserved for all other organisations.
            </span>
          </div>
        )}

        {/* Client admin note hint */}
        {isAdmin && !isPlatformOwner && (
          <div style={{
            background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6,
            padding: '10px 16px', fontSize: 12, color: '#166534',
            marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span>✎</span>
            <span>
              You can add your organisation's context, exemptions, or internal references to any clause using the <strong>Add note</strong> button. Notes are visible only to your organisation.
            </span>
          </div>
        )}

        {successMsg && (
          <div style={{
            background: '#d4edda', border: '1px solid #c3e6cb', borderRadius: 6,
            padding: '10px 16px', fontSize: 12, color: '#155724', marginBottom: 20,
          }}>
            {successMsg}
          </div>
        )}

        {saveError && (
          <div style={{
            background: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: 6,
            padding: '10px 16px', fontSize: 12, color: '#721c24', marginBottom: 20,
          }}>
            {saveError}
          </div>
        )}

        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#8a8a8a', fontSize: 13 }}>
            Loading regulatory library…
          </div>
        )}

        {!loading && error && (
          <div style={{
            background: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: 6,
            padding: '16px 20px', fontSize: 13, color: '#721c24',
          }}>
            {error}
          </div>
        )}

        {!loading && !error && Object.keys(grouped).length === 0 && (
          <div style={{
            background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 6,
            padding: '32px', textAlign: 'center', color: '#8a8a8a', fontSize: 13,
          }}>
            No regulatory clauses found. Run the Week 6 migration to seed the platform defaults.
          </div>
        )}

        {!loading && !error && Object.entries(grouped).map(([conditionCode, clauses]) => (
          <div key={conditionCode} style={{ marginBottom: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: GOLD, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                {conditionCode}
              </span>
              <span style={{ fontSize: 11, color: '#8a8a8a' }}>
                {clauses.length} clause{clauses.length !== 1 ? 's' : ''}
              </span>
              {updatedCodes.has(conditionCode) && (
                <span style={{
                  fontSize: 10, fontWeight: 700, color: '#fff',
                  background: GOLD, borderRadius: 10, padding: '2px 8px', letterSpacing: 0.3,
                }}>
                  Updated
                </span>
              )}
              <div style={{ flex: 1, height: 1, background: BORDER }} />
            </div>

            {clauses.map(clause => (
              <ClauseCard
                key={clause.id}
                clause={clause}
                isAdmin={isAdmin}
                isPlatformOwner={isPlatformOwner}
                isEditing={editingId === clause.id}
                isSaving={savingId === clause.id}
                isPlatformDefault={clause.org_id === null}
                hasUpdate={updatedCodes.has(clause.condition_code)}
                orgNote={notesMap[clause.id] || null}
                onEdit={c => { setEditingId(c.id); setSaveError(''); }}
                onSave={form => handleSave(clause.id, form)}
                onCancelEdit={() => { setEditingId(null); setSaveError(''); }}
                onViewHistory={handleViewHistory}
                onSaveNote={handleSaveNote}
                onDeleteNote={handleDeleteNote}
                savingNote={savingNote}
              />
            ))}
          </div>
        ))}

        {!loading && !error && refs.length > 0 && (
          <div style={{
            marginTop: 8, background: '#f8f6f2', border: `1px solid #e0dbd4`,
            borderLeft: `4px solid ${GOLD}`, borderRadius: '0 4px 4px 0', padding: '14px 18px',
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: NAVY, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.3 }}>
              Regulatory Reference Disclaimer
            </div>
            <div style={{ fontSize: 11, color: '#5a5a5a', lineHeight: 1.7 }}>
              Obligation summaries are authored by Flow Advisory Group and were verified against APRA CPS 230
              (effective 1 July 2025) and APRA CPS 234 (effective 1 July 2019) as at June 2026.
              APRA prudential standards are subject to amendment. For current standard text refer to{' '}
              <a href="https://apra.gov.au" target="_blank" rel="noreferrer" style={{ color: GOLD }}>apra.gov.au</a>{' '}
              and the{' '}
              <a href="https://handbook.apra.gov.au" target="_blank" rel="noreferrer" style={{ color: GOLD }}>APRA Prudential Handbook</a>.
              This page does not constitute legal or compliance advice.
            </div>
          </div>
        )}
      </div>

      {historyRefId && (
        <>
          <div
            onClick={() => setHistoryRefId(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 99 }}
          />
          <VersionHistory
            history={history}
            loading={historyLoading}
            onClose={() => setHistoryRefId(null)}
          />
        </>
      )}
    </div>
  );
}
